import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API from '../context/API';

// Types
export type TimeRange = '7d' | '30d' | '90d' | '1y';

export interface MerchantData {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  [key: string]: any;
}

export interface Order {
  id: string;
  total: string | number;
  amount?: string | number;
  price?: string | number;
  customer_id?: string;
  customerId?: string;
  user_id?: string;
  customer?: { id: string };
  items?: OrderItem[];
  [key: string]: any;
}

export interface OrderItem {
  item_id?: string;
  id?: string;
  price: string | number;
  quantity?: string | number;
  [key: string]: any;
}

export interface Category {
  id: string;
  category?: string;
  name?: string;
  [key: string]: any;
}

export interface Item {
  id: string;
  item?: string;
  name?: string;
  price: string | number;
  category_id?: string;
  [key: string]: any;
}

export interface Dispute {
  id: string;
  [key: string]: any;
}

export interface OverviewMetrics {
  totalSales: number;
  totalOrders: number;
  averageOrderValue: number;
  totalCustomers: number;
  growthRate: number;
  targetAchievement: number;
}

export interface TrendsData {
  sales: Order[];
  orders: Order[];
  customers: Order[];
}

export interface CategorySales {
  id: string;
  name: string;
  itemCount: number;
  totalValue: number;
  orderCount: number;
}

export interface ItemSales {
  id: string;
  name: string;
  price: number;
  soldCount: number;
  revenue: number;
  orderCount: number;
}

export interface TrendingItem {
  id: string;
  name: string;
  revenue: number;
  orderCount: number;
  [key: string]: any;
}

export interface SalesAnalytics {
  totalRevenue: number;
  topSellingItems: TrendingItem[];
  salesByCategory: CategorySales[];
}

export interface AnalyticsData {
  overview: OverviewMetrics;
  trends: TrendsData;
  categories: Category[];
  topItems: TrendingItem[];
  salesAnalytics?: SalesAnalytics;
  categorySales?: CategorySales[];
  itemSales?: ItemSales[];
  trendingItems?: TrendingItem[];
  disputes?: Dispute[];
}

interface MerchantAnalyticsState {
  merchantData: MerchantData | null;
  analyticsData: AnalyticsData | null;
  loading: boolean;
  analyticsLoading: boolean;
  lastUpdated: string;
  timeRange: TimeRange;
  error: string | null;
}

const initialState: MerchantAnalyticsState = {
  merchantData: null,
  analyticsData: null,
  loading: false,
  analyticsLoading: false,
  lastUpdated: '',
  timeRange: '7d',
  error: null,
};

// Helper function to get date range
const getDateRange = (range: TimeRange) => {
  const now = new Date();
  const startDate = new Date();

  switch (range) {
    case '7d':
      startDate.setDate(now.getDate() - 7);
      break;
    case '30d':
      startDate.setDate(now.getDate() - 30);
      break;
    case '90d':
      startDate.setDate(now.getDate() - 90);
      break;
    case '1y':
      startDate.setFullYear(now.getFullYear() - 1);
      break;
    default:
      startDate.setDate(now.getDate() - 7);
  }

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: now.toISOString().split('T')[0],
  };
};

// Async thunks
export const fetchMerchantData = createAsyncThunk<
  { data: MerchantData; lastUpdated: string },
  void,
  { rejectValue: string }
>('merchantAnalytics/fetchMerchantData', async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await API.get('merchant/profile', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const now = Date.now();
    localStorage.setItem('lastUpdatedTimestamp', now.toString());
    return {
      data: response.data.data,
      lastUpdated: new Date(now).toISOString(),
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch merchant profile data'
    );
  }
});

export const fetchAnalyticsData = createAsyncThunk<
  AnalyticsData,
  TimeRange,
  { rejectValue: string }
>('merchantAnalytics/fetchAnalyticsData', async (range, { rejectWithValue }) => {
  try {
    const { startDate, endDate } = getDateRange(range);

    // Fetch data from multiple endpoints
    // const [storesRes, customizationsRes, categoriesRes, topItemsRes, disputesRes, ordersRes] =
    const [customizationsRes, categoriesRes, topItemsRes, ordersRes] =
      await Promise.allSettled([
        // API.get('/stores'), // Commented out temporarily
        API.get('/customizations'),
        API.get('/categories'),
        API.get('/items'),
        // API.get('/analytics/disputes'), // Commented out temporarily
        API.get('/orders', { params: { startDate, endDate } }),
      ]);

    // Process results
    // const storesData = storesRes.status === 'fulfilled' ? storesRes.value.data : null;
    const customizationsData =
      customizationsRes.status === 'fulfilled' ? customizationsRes.value.data : null;
    const categoriesData =
      categoriesRes.status === 'fulfilled' ? categoriesRes.value.data : null;
    const itemsData = topItemsRes.status === 'fulfilled' ? topItemsRes.value.data : null;
    // const disputesData =
    //   disputesRes.status === 'fulfilled' ? disputesRes.value.data : null;
    const ordersData = ordersRes.status === 'fulfilled' ? ordersRes.value.data : null;

    // Extract data arrays
    // const stores = storesData?.data || [];
    const customizations = customizationsData?.data || [];
    const categories = categoriesData?.data || [];
    const items = itemsData?.data || [];
    // const disputes = disputesData?.data || disputesData?.disputes || [];
    const disputes: Dispute[] = [];
    const orders = ordersData?.data || [];

    // Calculate metrics
    const totalSales = orders.reduce((sum: number, order: Order) => {
      const orderTotal =
        parseFloat(String(order.total)) ||
        parseFloat(String(order.amount)) ||
        parseFloat(String(order.price)) ||
        0;
      return sum + orderTotal;
    }, 0);

    const totalOrders = orders.length;
    const averageOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;

    const uniqueCustomers = new Set(
      orders.map(
        (order: Order) =>
          order.customer_id || order.customerId || order.user_id || order.customer?.id
      )
    ).size;

    // Calculate item sales
    const itemSales: ItemSales[] = items.map((item: Item) => {
      const itemOrders = orders.filter((order: Order) => {
        if (order.items && Array.isArray(order.items)) {
          return order.items.some(
            (orderItem: OrderItem) =>
              orderItem.item_id === item.id || orderItem.id === item.id
          );
        }
        return false;
      });

      const revenue = itemOrders.reduce((sum: number, order: Order) => {
        const orderItems = order.items || [];
        const itemInOrder = orderItems.find(
          (orderItem: OrderItem) =>
            orderItem.item_id === item.id || orderItem.id === item.id
        );
        if (itemInOrder) {
          return (
            sum +
            parseFloat(String(itemInOrder.price)) * parseFloat(String(itemInOrder.quantity || 1))
          );
        }
        return sum;
      }, 0);

      const soldCount = itemOrders.reduce((sum: number, order: Order) => {
        const orderItems = order.items || [];
        const itemInOrder = orderItems.find(
          (orderItem: OrderItem) =>
            orderItem.item_id === item.id || orderItem.id === item.id
        );
        if (itemInOrder) {
          return sum + parseFloat(String(itemInOrder.quantity || 1));
        }
        return sum;
      }, 0);

      return {
        id: item.id,
        name: item.item || item.name || '',
        price: parseFloat(String(item.price)) || 0,
        soldCount,
        revenue,
        orderCount: itemOrders.length,
      };
    });

    const trendingItems: TrendingItem[] = itemSales
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10)
      .map((item) => ({
        id: item.id,
        name: item.name,
        revenue: item.revenue,
        orderCount: item.orderCount,
      }));

    // Calculate category sales
    const categorySales: CategorySales[] = categories.map((category: Category) => {
      const categoryItems = items.filter(
        (item: Item) => item.category_id === category.id
      );
      const categoryOrders = orders.filter((order: Order) => {
        if (order.items && Array.isArray(order.items)) {
          return order.items.some((orderItem: OrderItem) => {
            const item = items.find(
              (i: Item) => i.id === orderItem.item_id || i.id === orderItem.id
            );
            return item && item.category_id === category.id;
          });
        }
        return false;
      });

      const totalValue = categoryOrders.reduce((sum: number, order: Order) => {
        const orderItems = order.items || [];
        const categoryItemsInOrder = orderItems.filter((orderItem: OrderItem) => {
          const item = items.find(
            (i: Item) => i.id === orderItem.item_id || i.id === orderItem.id
          );
          return item && item.category_id === category.id;
        });

        return (
          sum +
          categoryItemsInOrder.reduce((itemSum: number, orderItem: OrderItem) => {
            return (
              itemSum +
              parseFloat(String(orderItem.price)) *
                parseFloat(String(orderItem.quantity || 1))
            );
          }, 0)
        );
      }, 0);

      return {
        id: category.id,
        name: category.category || category.name || '',
        itemCount: categoryItems.length,
        totalValue,
        orderCount: categoryOrders.length,
      };
    });

    // Sales analytics
    const salesAnalytics: SalesAnalytics = {
      totalRevenue: totalSales,
      topSellingItems: trendingItems,
      salesByCategory: categorySales,
    };

    return {
      overview: {
        totalSales,
        totalOrders,
        averageOrderValue,
        totalCustomers: uniqueCustomers,
        growthRate: 0,
        targetAchievement: 0,
      },
      trends: {
        sales: orders,
        orders: orders,
        customers: orders,
      },
      categories,
      topItems: trendingItems,
      salesAnalytics,
      categorySales,
      itemSales,
      trendingItems,
      disputes,
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch analytics data'
    );
  }
});

const merchantAnalyticsSlice = createSlice({
  name: 'merchantAnalytics',
  initialState,
  reducers: {
    setTimeRange: (state, action: PayloadAction<TimeRange>) => {
      state.timeRange = action.payload;
    },
    setLastUpdated: (state, action: PayloadAction<string>) => {
      state.lastUpdated = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch merchant data
      .addCase(fetchMerchantData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMerchantData.fulfilled, (state, action) => {
        state.loading = false;
        state.merchantData = action.payload.data;
      })
      .addCase(fetchMerchantData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch analytics data
      .addCase(fetchAnalyticsData.pending, (state) => {
        state.analyticsLoading = true;
        state.error = null;
      })
      .addCase(fetchAnalyticsData.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.analyticsData = action.payload;
      })
      .addCase(fetchAnalyticsData.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setTimeRange, setLastUpdated, clearError } = merchantAnalyticsSlice.actions;
export default merchantAnalyticsSlice.reducer;

